import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { useSelector, useDispatch } from 'react-redux'
import {
  Button,
  ConfirmationPopup,
  IconDelete,
  IconEdit,
  IconAdd,
  List,
  TooltipHeader,
  Notification,
  PermissionMissingNotificationTitle
} from 'shared/components'
import CustomFieldEnumTag from '../../../Forms/CustomFieldEnumTag'
import {
  isFeatureEnabled,
  getDisabledMessage,
  isFeatureAvailable,
  sendErrorReport,
  checkUserPermission
} from 'shared/helpers'
import { platformFeatures, userPermissions } from 'shared/constants'
import { fetchCustomFields, deleteCustomField, getAllProducts } from 'src/product/actions'
import { CustomFieldForm, CustomFieldEnumForm } from '../../../Forms'
import './styles.scss'

const CustomFields = ({ companyID, product }) => {
  const canManageProducts = checkUserPermission(userPermissions.products_write)
  const dispatch = useDispatch()
  const productID = get(product, 'id')
  const companyFeatures = useSelector(state => get(state, 'company.details.features'))
  const customFieldsFeature = companyFeatures.find(item => item.key === 'extra:custom-fields')
  const customFieldsValue = get(customFieldsFeature, 'value')
  const planMessage =
    customFieldsValue === 0
      ? __('Custom Fields are not available in your plan.')
      : customFieldsValue === -1
      ? __('Your plan allows for unlimited custom fields.')
      : __(`Your plan allows for ${customFieldsValue} custom fields.`)

  const [isLoading, setLoading] = useState(true)
  const [customFields, setCustomFields] = useState([])
  const [showCustomFieldsForm, setCustomFieldsFormDisplay] = useState(false)
  const [initialEnums, setInitialEnums] = useState([])
  const [showCustomFieldsEnumForm, setCustomFieldsEnumFormDisplay] = useState(false)
  const [fieldToDelete, setFieldToDelete] = useState(null)
  const [isDeleteConfirmationDisplayed, setDeleteConfirmationDisplay] = useState(false)
  const [isDeleting, setDeleting] = useState(false)
  const [fieldToEdit, setFieldToEdit] = useState(null)

  // TODO add serverside pagination
  const getCustomFields = useCallback(() => {
    fetchCustomFields(productID, companyID)
      .then(res => {
        setLoading(false)
        setCustomFields(get(res, 'data') || [])
      })
      .catch(err => {
        setLoading(false)
        sendErrorReport(err, 'Cannot fetch product custom fields')
        Notification(
          'error',
          __('Error occured'),
          __('We could not load the product custom fields.')
        )
      })
  }, [productID])

  const handleCustomFieldDelete = () => {
    const fieldToDeleteID = get(fieldToDelete, 'id')
    setDeleting(true)
    deleteCustomField(fieldToDeleteID, companyID)
      .then(() => {
        getCustomFields()
        setDeleteConfirmationDisplay(false)
        setFieldToDelete(null)
        setDeleting(false)
        Notification('success', __('Custom field deleted'))
        dispatch(getAllProducts(companyID))
      })
      .catch(err => {
        setDeleting(false)
        sendErrorReport(err, 'Cannot delete product custom fields')
        Notification(
          'error',
          __('Error occured'),
          __('We could not delete the product custom fields.')
        )
      })
  }

  useEffect(() => {
    getCustomFields()
  }, [getCustomFields])

  const handleManageCustomFieldClick = cb => {
    if (!canManageProducts) {
      Notification(
        'error',
        <PermissionMissingNotificationTitle permission={userPermissions.products_write} />,
        __('Contact you account admin for support.')
      )
      return false
    }
    cb(true)
    return true
  }

  return (
    <div className='CustomFields custom-fields-tab'>
      <div className='Tab-header'>
        {__('Custom Fields allow you to push information from the platform to your application.')}
      </div>
      <Button
        featureEnabled={isFeatureEnabled(platformFeatures.extra_custom_fields)}
        notEnabledMessage={getDisabledMessage()}
        featureAvailable={isFeatureAvailable(
          platformFeatures.extra_custom_fields,
          customFields.count
        )}
        notAvailableMessage={planMessage}
        ctaText={__('Upgrade to add more custom fields.')}
        onClick={() => handleManageCustomFieldClick(setCustomFieldsFormDisplay)}
        size='sm'
        theme='info'
      >
        {__('Add custom field')}
      </Button>
      <List
        columns={[
          { accessor: 'name', Header: __('Name') },
          { accessor: 'default_value', Header: __('Value') },
          {
            accessor: 'product_custom_field_enums',
            Header: () =>
              TooltipHeader(
                __('Allowed values'),
                __(
                  'Define a list of values that can be assigned to this custom field on a license.'
                )
              ),
            Cell: rowData => {
              const productCustomFieldEnums =
                get(rowData, 'original.product_custom_field_enums') || []
              if (!productCustomFieldEnums.length) {
                return (
                  <div>
                    <Button
                      featureEnabled={isFeatureEnabled(platformFeatures.platform_edit_license)}
                      featureAvailable={isFeatureAvailable(platformFeatures.platform_edit_license)}
                      notEnabledMessage={getDisabledMessage()}
                      className='enum-button'
                      onClick={() =>
                        handleManageCustomFieldClick(() => {
                          const productCustomFieldEnums =
                            get(rowData, 'original.product_custom_field_enums') || []
                          const enumValues = productCustomFieldEnums.map(
                            enumValue => enumValue.value
                          )
                          setFieldToEdit(rowData.original)
                          setInitialEnums(enumValues)
                          setCustomFieldsEnumFormDisplay(true)
                        })
                      }
                      type='button'
                    >
                      <div className='content'>
                        <IconAdd height='18px' width='18px' viewBox='0 0 24 24' stroke='#242424' />
                        <span>{__('Add allowed values')}</span>
                      </div>
                    </Button>
                  </div>
                )
              }
              return (
                <div className='enum-tags-container'>
                  {productCustomFieldEnums.map(enumValue => (
                    <CustomFieldEnumTag
                      key={enumValue.id}
                      value={enumValue.value}
                      enumId={enumValue.id}
                      companyId={companyID}
                      onDelete={() => getCustomFields()}
                    />
                  ))}
                  <Button
                    featureEnabled={isFeatureEnabled(platformFeatures.platform_edit_license)}
                    featureAvailable={isFeatureAvailable(platformFeatures.platform_edit_license)}
                    notEnabledMessage={getDisabledMessage()}
                    className='enum-button'
                    onClick={() =>
                      handleManageCustomFieldClick(() => {
                        setFieldToEdit(rowData.original)
                        setInitialEnums(productCustomFieldEnums.map(e => e.value))
                        setCustomFieldsEnumFormDisplay(true)
                      })
                    }
                    type='button'
                  >
                    <div className='content'>
                      <IconAdd height='18px' width='18px' viewBox='0 0 24 24' stroke='#242424' />
                      <span>{__('Add more')}</span>
                    </div>
                  </Button>
                </div>
              )
            }
          },
          {
            className: 'text-center',
            headerClassName: 'text-center',
            Cell: rowData => (
              <Button
                featureEnabled={isFeatureEnabled(platformFeatures.platform_edit_license)}
                featureAvailable={isFeatureAvailable(platformFeatures.platform_edit_license)}
                notEnabledMessage={getDisabledMessage()}
                className='edit-button'
                onClick={() =>
                  handleManageCustomFieldClick(() => {
                    setFieldToEdit(rowData.original)
                    setCustomFieldsFormDisplay(true)
                  })
                }
                type='button'
              >
                <IconEdit height='16px' width='16px' />
              </Button>
            ),
            maxWidth: 50
          },
          {
            className: 'text-center',
            headerClassName: 'text-center',
            Cell: rowData => (
              <Button
                featureEnabled={isFeatureEnabled(platformFeatures.platform_edit_product)}
                notEnabledMessage={getDisabledMessage()}
                className='edit-button'
                onClick={() =>
                  handleManageCustomFieldClick(() => {
                    setFieldToDelete(rowData.original)
                    setDeleteConfirmationDisplay(true)
                  })
                }
                type='button'
              >
                <IconDelete height='16px' width='16px' color='#ee5253' />
              </Button>
            ),
            maxWidth: 50
          }
        ]}
        data={get(customFields, 'results') || []}
        minRows={2}
        pageSize={20}
        loading={isLoading}
      />
      {showCustomFieldsEnumForm && (
        <CustomFieldEnumForm
          closeCb={() => {
            setCustomFieldsEnumFormDisplay(false)
          }}
          confirmCb={() => {
            getCustomFields()
            setCustomFieldsEnumFormDisplay(false)
          }}
          companyID={companyID}
          productCustomField={fieldToEdit}
          initialEnums={initialEnums}
        />
      )}
      {showCustomFieldsForm && (
        <CustomFieldForm
          closeCb={() => {
            setCustomFieldsFormDisplay(false)
            setFieldToEdit(null)
          }}
          refetchCustomFields={getCustomFields}
          productId={productID}
          updateProducts={() => dispatch(getAllProducts(companyID))}
          fieldToEdit={fieldToEdit}
          companyID={companyID}
        />
      )}
      {isDeleteConfirmationDisplayed && (
        <ConfirmationPopup
          closeCb={() => {
            setDeleteConfirmationDisplay(false)
            setFieldToDelete(null)
          }}
          confirmCb={handleCustomFieldDelete}
          title={`${__('Are you sure you want to delete custom field:')} ${get(
            fieldToDelete,
            'name'
          )}?`}
          confirmText={__('Delete')}
          theme='error'
          disabled={isDeleting}
        />
      )}
    </div>
  )
}

CustomFields.propTypes = {
  companyID: PropTypes.number.isRequired,
  product: PropTypes.object.isRequired
}

export default CustomFields
